import cx from 'classnames';
import { Fragment } from 'react';

import { ItineraryCard } from '../../../design-system/components/card/itinerary/ItineraryCard';
import { Picture } from '../../../domains/Picture/Picture';
import { sortedAgenciesByBetterTripIndex } from '../../../resources/agency/domain/operations/sortedAgenciesByBetterTripIndex';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { Itinerary } from '../../../resources/itinerary/domain/types/Itinerary';
import { PointOfInterest } from '../../../resources/pointOfInterest/domain/types/PointOfInterest';
import { AgencyCard } from '../../../shared/components/AgencyCard/AgencyCard';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';
import { useItineraryActivityTypes } from '../shared/hooks/useItineraryActivityTypes';

import style from './ItinerariesGrid.module.css';
import { ItineraryCardSkeleton } from './ItineraryCardSkeleton';
import { LocalHeroBlockInfo } from './LocalHeroBlockInfo';

type ItinerariesGridProps = {
    selectedFilters: Array<{ name: string; translation: string }>;
    isLoading: boolean;
    destination: PickDomain<
        Destination,
        'id' | 'slug',
        {
            geolocation: Destination['geolocation'] | null;
        }
    >;
    itineraries: Array<
        PickDomain<
            Itinerary,
            | 'id'
            | 'startingPrice'
            | 'duration'
            | 'isGIR'
            | 'title'
            | 'rangeName'
            | 'picture'
            | 'slug'
            | 'activityTypes',
            {
                pointsOfInterest: Array<
                    PickDomain<
                        PointOfInterest,
                        'name' | 'geolocation' | 'id',
                        {
                            picture: Picture | null;
                        }
                    >
                >;
                destination: PickDomain<Itinerary['destination'], 'id' | 'slug'>;
                agency: PickDomain<
                    Itinerary['agency'],
                    | 'id'
                    | 'picture'
                    | 'name'
                    | 'slug'
                    | 'nameTranslationRule'
                    | 'isLocalHero'
                    | 'betterTripIndex'
                    | 'tripReviewsAverageRating'
                    | 'description',
                    { destination: PickDomain<Destination, 'id' | 'slug'> }
                >;
                typeOfProfile: Itinerary['typeOfProfile'] | null;
            }
        >
    >;
    onItineraryHovered: (itinerary: ItinerariesGridProps['itineraries'][number]) => void;
};

const AGENCY_SHOWN_AT_ITINERARIES_INTERVAL = 7;

type ItineraryCardWithTagsProps = {
    itinerary: ItinerariesGridProps['itineraries'][number];
    selectedFilters: ItinerariesGridProps['selectedFilters'];
    index: number;
    onMouseEnter: () => void;
};

function ItineraryCardWithTags({
    itinerary,
    selectedFilters,
    index,
    onMouseEnter,
}: ItineraryCardWithTagsProps) {
    const activityTypes = useItineraryActivityTypes({
        itinerary,
        selectedFilters,
    });

    return (
        <div data-itinerary-index={index} onMouseEnter={onMouseEnter}>
            <ItineraryCard
                image={{ sizes: '300px' }}
                itinerary={{
                    ...itinerary,
                    activityTypes,
                }}
                titleElement="h3"
                showActivityTypes={true}
                showPOI={false}
                trackingInformation={{
                    click: {
                        card_number: index + 1,
                        block: 'itineraries',
                        row_number: 0,
                    },
                    wishListAdded: {
                        block: 'itineraries',
                    },
                }}
            />
        </div>
    );
}

export const ItinerariesGrid = ({
    itineraries,
    destination,
    isLoading,
    selectedFilters,
    onItineraryHovered,
}: ItinerariesGridProps) => {
    const agencies = sortedAgenciesByBetterTripIndex(
        Array.from(
            new Set(
                itineraries.map((itinerary) => {
                    return itinerary.agency.id;
                }),
            ),
        )
            .map((id) => {
                const itinerary = itineraries.find((itinerary) => {
                    return itinerary.agency.id === id;
                })!;

                return itinerary.agency;
            })
            .filter((agency) => {
                return agency.destination.id === destination.id;
            }),
    );

    return (
        <div className={cx(style.containerGrid)}>
            {isLoading ? (
                <>
                    <ItineraryCardSkeleton />
                    <ItineraryCardSkeleton />
                    <ItineraryCardSkeleton />
                    <ItineraryCardSkeleton />
                </>
            ) : (
                <>
                    {itineraries.some((iti) => iti.agency.isLocalHero) ? (
                        <LocalHeroBlockInfo />
                    ) : null}
                    {itineraries.map((itinerary, index) => {
                        // Calculate the index of the agency card to display based on the current itinerary index
                        const displayedAgencyCardIndex =
                            Math.floor((index + 1) / AGENCY_SHOWN_AT_ITINERARIES_INTERVAL) - 1;

                        // Check if the current itinerary is a multiple of 7 because we want to display an agency card every 7 itineraries
                        const shouldDisplayAgencyCard =
                            (index + 1) % AGENCY_SHOWN_AT_ITINERARIES_INTERVAL === 0 &&
                            agencies[displayedAgencyCardIndex];

                        return (
                            <Fragment key={itinerary.id}>
                                <ItineraryCardWithTags
                                    itinerary={itinerary}
                                    selectedFilters={selectedFilters}
                                    index={index}
                                    onMouseEnter={() => {
                                        onItineraryHovered(itinerary);
                                    }}
                                />
                                {shouldDisplayAgencyCard ? (
                                    <div key={`agency-${displayedAgencyCardIndex}`}>
                                        <AgencyCard
                                            key={agencies[displayedAgencyCardIndex].id}
                                            agency={agencies[displayedAgencyCardIndex]}
                                            image={{
                                                priority: false,
                                                sizes: '300px',
                                            }}
                                            trackingInformation={{
                                                click: {
                                                    agency_id:
                                                        agencies[displayedAgencyCardIndex].id,
                                                    agency_slug:
                                                        agencies[displayedAgencyCardIndex].slug,
                                                    agency_better_trip_index:
                                                        agencies[displayedAgencyCardIndex]
                                                            .betterTripIndex,
                                                    block: 'itineraries',
                                                    card_number: index + 2,
                                                },
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </Fragment>
                        );
                    })}
                </>
            )}
        </div>
    );
};
